import React, { useEffect, useState } from "react";
import { FiShoppingCart, FiHeart } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import pm from "./images/products.webp";
import "./ProductCard.css";
import LoginModal from "./Login/Login";
import axiosInstance from "../utils/axiosInstance";
import { addWishlistItem, removeWishlistItem } from "../redux/wishlistSlice";

const formatProductPrice = (value) => `$${Number(value).toLocaleString("en-US")}`;


export const getProductImage = (product) =>
  product.images?.[0]?.image?.image || product.product?.image?.image || pm;

const getDiscountPercent = (product) => {
  const discount = Number(product.discount_percentage || product.product?.discount_percentage);
  const offerPrice = Number(product.prices?.sale_offer_price);
  const salePrice = Number(product.prices?.sale_price);

  if (Number.isFinite(discount) && discount > 0) {
    return Math.round(discount);
  }

  if (offerPrice > 0 && salePrice > offerPrice) {
    return Math.round(((salePrice - offerPrice) / salePrice) * 100);
  }

  return null;
};

export function getVariantStockStatus(product, mode = "buy") {
  const raw = mode === "rent" ? product.rental_stock : product.sale_stock;
  const effectiveStock = Number(raw);
  const stockKnown = Number.isFinite(effectiveStock);

  const isOutOfStock =
    (stockKnown && effectiveStock <= 0) ||
    (!stockKnown && product.availability === false);

  const isLimitedStock =
    stockKnown && effectiveStock >= 1 && effectiveStock <= 10 && !isOutOfStock;

  return { effectiveStock, isOutOfStock, isLimitedStock };
}

const getWishlistApiType = (mode) => (mode === "rent" ? "rental" : "sale"); // for POST and CHECK API calls
const getWishlistReduxType = (mode) => (mode === "rent" ? "rent" : "buy");   // for Redux local state

function ProductCard({ product, mode = "buy", onOpen, onAdd, onEnquire }) {
  const dispatch = useDispatch();
  const [loginShow, setLoginShow] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const wishlistItems = useSelector((store) => store.wishlist?.items || []);
  const isWishlisted = wishlistItems.some(
    (item) => String(item.id) === String(product.id) && item.type === getWishlistReduxType(mode)
  );

  /* Seed Redux wishlist state from server on mount (for logged-in users) */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userid");
    if (!token || !userId || !product?.id) return;

    axiosInstance
      .get("catlog/wishlist-check/", {
        params: { variant_id: product.id, type: getWishlistApiType(mode) },
      })
      .then((res) => {
        const itemType = getWishlistReduxType(mode);
        const alreadyInStore = wishlistItems.some(
          (item) => String(item.id) === String(product.id) && item.type === itemType
        );
        if (res.data?.in_wishlist && !alreadyInStore) {
          dispatch(addWishlistItem(getWishlistProduct()));
        } else if (!res.data?.in_wishlist && alreadyInStore) {
          dispatch(removeWishlistItem({ id: product.id, type: itemType }));
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, mode]);

  const buyCart = useSelector((store) => store.cart?.buyCart || []);
  const rentCart = useSelector((store) => store.cart?.rentCart || []);

  const buyItem = buyCart.find((item) => item.id === product.id);
  const buyQty = buyItem ? buyItem.qty : 0;

  const rentItems = rentCart.filter(item => item.id === product.id);
  const totalRentQty = rentItems.reduce((acc, item) => acc + item.qty, 0);

  const currentQty = mode === "buy" ? buyQty : totalRentQty;

  const handleDecrease = (e) => {
    e.stopPropagation();
    if (mode === "buy") {
      dispatch({ type: "cart/decreaseBuyQty", payload: product.id });
    } else {
      if (rentItems.length > 0) {
        dispatch({ type: "cart/decreaseRentQty", payload: rentItems[0].cartItemId });
      }
    }
  };

  const handleIncrease = (e) => {
    e.stopPropagation();
    if (mode === "rent") {
      onOpen(product);
    } else {
      onAdd(e, product);
    }
  };

  const getWishlistProduct = () => ({
    id: product.id,
    name: product.name,
    image: getProductImage(product),
    slug: product.slug || product.product?.slug,
    friendlyurl: product.slug || product.product?.slug,
    type: mode === "rent" ? "rent" : "buy",
    offerPrice:
      mode === "rent"
        ? Number(product.prices?.rental_price || 0)
        : Number(product.prices?.sale_offer_price || product.prices?.sale_price || 0),
    sale_offer_price: product.prices?.sale_offer_price,
    sale_price: product.prices?.sale_price,
    rental_price: product.prices?.rental_price,
  });

  const handleWishlist = async (event) => {
    event?.stopPropagation?.();
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userid");

    if (!token || !userId) {
      setLoginShow(true);
      return;
    }

    if (wishlistLoading) return;

    setWishlistLoading(true);

    try {
      if (product._wishlistId) {
        await axiosInstance.delete(`catlog/wishlists/${product._wishlistId}/`);
        const removeType = getWishlistReduxType(mode);
        dispatch(removeWishlistItem({ id: product.id, type: removeType }));
        window.dispatchEvent(new CustomEvent("wishlistUpdated", { detail: { removedId: product.id, removedType: removeType } }));
        toast.success("Removed from wishlist");
        return;
      }

      /* Always check server first so we have the real wishlist ID for deletion */
      const checkRes = await axiosInstance.get("catlog/wishlist-check/", {
        params: { variant_id: product.id, type: getWishlistApiType(mode) },
      });
      const serverInWishlist = checkRes.data?.in_wishlist;
      const wishlistId = checkRes.data?.id;

      if (serverInWishlist && wishlistId) {
        await axiosInstance.delete(`catlog/wishlists/${wishlistId}/`);
        const removeType = getWishlistReduxType(mode);
        dispatch(removeWishlistItem({ id: product.id, type: removeType }));
        window.dispatchEvent(new CustomEvent("wishlistUpdated", { detail: { removedId: product.id, removedType: removeType } }));
        toast.success("Removed from wishlist");
      } else if (!serverInWishlist) {
        await axiosInstance.post("catlog/wishlists/", {
          user: userId,
          varient: product.id,
          type: getWishlistApiType(mode),
        });
        dispatch(addWishlistItem(getWishlistProduct()));
        window.dispatchEvent(new CustomEvent("wishlistUpdated", { detail: { addedId: product.id } }));
        toast.success("Added to wishlist");
      } else {
        /* Server says in wishlist but no ID — fall back to local remove */
        const removeType = getWishlistReduxType(mode);
        dispatch(removeWishlistItem({ id: product.id, type: removeType }));
        window.dispatchEvent(new CustomEvent("wishlistUpdated", { detail: { removedId: product.id, removedType: removeType } }));
        toast.success("Removed from wishlist");
      }
    } catch {
      toast.error("Could not update wishlist. Please try again.");
    } finally {
      setWishlistLoading(false);
    }
  };

  const renderPrice = () => {
    const offerPrice = product.prices?.sale_offer_price;
    const salePrice = product.prices?.sale_price;

    const rentalPrice = Number(product.prices?.rental_price || 0);
    const rentalOfferPrice = Number(product.prices?.rental_offer_price || 0);

    if (mode === "rent" && rentalPrice) {
      return (
        <div className="pc-price-wrap">
          <span className="pc-price">
            {formatProductPrice(
              rentalOfferPrice > 0 && rentalOfferPrice < rentalPrice
                ? rentalOfferPrice
                : rentalPrice
            )}
          </span>

          {rentalOfferPrice > 0 && rentalOfferPrice < rentalPrice && (
            <del className="pc-original">
              {formatProductPrice(rentalPrice)}
            </del>
          )}

          <span className="pc-per-day">/ day</span>
        </div>
      );
    }

    if (
      offerPrice &&
      salePrice &&
      Number(offerPrice) < Number(salePrice)
    ) {
      return (
        <div className="pc-price-wrap">
          <span className="pc-price">
            {formatProductPrice(offerPrice)}
          </span>

          <del className="pc-original">
            {formatProductPrice(salePrice)}
          </del>
        </div>
      );
    }

    if (salePrice) {
      return (
        <div className="pc-price-wrap">
          <span className="pc-price">{formatProductPrice(salePrice)}</span>
        </div>
      );
    }

    return (
      <div className="pc-price-wrap">
        <span className="pc-price pc-price-quote">Quote on request</span>
      </div>
    );
  };

  const discountPercent = getDiscountPercent(product);
  const { isOutOfStock, isLimitedStock } = getVariantStockStatus(product, mode);

  return (
    <article
      className="pc-card"
      role="button"
      tabIndex={0}
      aria-label={isOutOfStock ? `${product.name}, out of stock` : undefined}
      onClick={() => onOpen(product)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(product); }}
    >
      {/* ── image ── */}
      <div className="pc-img-wrap">
        <div className="pc-badges-container">
          {discountPercent >= 1 && (
            <span className="pc-badge pc-badge-discount">
              {discountPercent}% OFF
            </span>
          )}
        </div>

        {isOutOfStock && (
          <span className="pc-badge pc-badge-oos pc-badge-right">Out of stock</span>
        )}

        <img
          src={getProductImage(product)}
          alt=""
          className="pc-img"
          style={{ opacity: isOutOfStock ? 0.5 : 1 }}
          onError={(e) => { e.target.src = pm; }}
        />
      </div>

      {/* ── body ── */}
      <div className="pc-body">
        {/* listing type pill */}
        {product.listing_type && (
          <span className="pc-listing-type">
            {product.listing_type === "both" ? "Buy / Rent" : product.listing_type}
          </span>
        )}

        <h3 className="pc-name">{product.name}</h3>

        <div className="pc-meta-wrap">
          {product.sku_code && (
            <p className="pc-sku">SKU: {product.sku_code}</p>
          )}

          {!!product.options?.length && (
            <div className="pc-variants">
              {product.options.map((option) => (
                <div key={option.id} className="pc-variant-item">
                  <span className="pc-variant-label">
                    {option.variant_type}:
                  </span>

                  <span className="pc-variant-value">
                    {option.variant_option}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* price row */}
        {renderPrice()}

        {!isOutOfStock && isLimitedStock && (
          <p className="pc-limited-text">Only a few left!</p>
        )}

        {/* actions */}

        <div className="pc-actions">
          {currentQty > 0 ? (
            <div style={{ flex: 1, display: "flex", height: "36px", alignItems: "center", border: "1.5px solid #fe7900", borderRadius: "6px", overflow: "hidden", background: "#fff" }}>
              <button
                type="button"
                style={{ width: "40%", height: "100%", background: "#fe7900", color: "#fff", border: "none", fontSize: "18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                onClick={handleDecrease}
              >
                -
              </button>
              <div style={{ width: "20%", minWidth: "24px", textAlign: "center", fontWeight: "bold", color: "#1a1f36", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {currentQty}
              </div>
              <button
                type="button"
                style={{ width: "40%", height: "100%", background: "#fe7900", color: "#fff", border: "none", fontSize: "18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                onClick={handleIncrease}
              >
                +
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="pc-btn-add"
              disabled={false}
              onClick={(e) => {
                e.stopPropagation();
                if (mode === "rent") {
                  onOpen(product);
                } else {
                  onAdd(e, product);
                }
              }}
            >
              <FiShoppingCart />
              Add to Cart
            </button>
          )}
          <button
            type="button"
            className={`pc-btn-wish${isWishlisted ? " active" : ""}`}
            disabled={wishlistLoading}
            aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
            onClick={handleWishlist}
          >
            {isWishlisted ? <FaHeart color="#e74c3c" style={{ color: "#e74c3c", fill: "#e74c3c" }} /> : <FiHeart color="currentColor" />}
          </button>
        </div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <LoginModal
          show={loginShow}
          onHide={() => setLoginShow(false)}
          onLoginSuccess={() => handleWishlist({ stopPropagation: () => { } })}
        />
      </div>
    </article>
  );
}

export default ProductCard;